import { CallbackKey, Callbacks, AppPreferences } from './types'
import { setPlatformGlobals } from './utils'

export const logMissingCallback = (
  key: CallbackKey,
  ...args: Parameters<Callbacks[CallbackKey]>
) => {
  console.warn(
    `⚠️ Skedulo Horizon Framework was unable to find a "${key}" callback`,
    args.length ? { ...args } : undefined,
  )
}

export const defaultCallbacks: Readonly<Callbacks> = {
  hasPermission: (permission) => {
    logMissingCallback('hasPermission', permission)
    return false
  },
  hasPermissions: (permissions) => {
    logMissingCallback('hasPermissions', permissions)
    return false
  },
  hasRole: (role) => {
    logMissingCallback('hasRole', role)
    return false
  },
  hasRoles: (roles) => {
    logMissingCallback('hasRoles', roles)
    return false
  },
  hasFeature: (flag) => {
    logMissingCallback('hasFeature', flag)
    return false
  },
  hasDevFlag: (flag) => {
    logMissingCallback('hasDevFlag', flag)
    return false
  },
  buildPlatformUrl: (slug) => {
    // Feels odd that this is treated differently to the other callbacks but it
    // does make sense for this to have a functional default in this case.
    return `/platform/page/${slug}`
  },
  handleNotFound: () => {
    logMissingCallback('handleNotFound')
  },
  getOrgPreferences: () => {
    logMissingCallback('getOrgPreferences')
    return null
  },
  getAppPreferences: (): AppPreferences | null => {
    logMissingCallback('getAppPreferences')
    return null
  },
  getSupportEmail: () => {
    logMissingCallback('getSupportEmail')
    return undefined
  },
  getShowIfFieldsFor: (
    job: unknown,
    customFieldsMapping: unknown,
    additionalFields: unknown,
  ) => {
    logMissingCallback(
      'getShowIfFieldsFor',
      job,
      customFieldsMapping,
      additionalFields,
    )
    return null
  },
}

export const allowedCallbackKeys = Object.keys(
  defaultCallbacks,
) as readonly CallbackKey[]

export type WrappedCallback<
  Callback extends Callbacks[CallbackKey] = Callbacks[CallbackKey],
> = (...args: Parameters<Callback>) => ReturnType<Callback>

/**
 * Provides callbacks for functionality that exists outside the framework and
 * consumed internally.
 *
 * These functions are implemented by the host application when the framework is
 * initialised. We provide default implementations as fallbacks in case they are
 * not implemented by the host.
 *
 * Horizon Core creates the instance and provides it through Context. Hosts set
 * callbacks in a root `HorizonConfig`, or directly with the `init` method.
 */
export class HostCallbacks implements Callbacks {
  private callbacksMap = new Map<CallbackKey, Callbacks[CallbackKey]>()

  constructor() {
    const self = this as unknown as Record<
      CallbackKey,
      (...args: never[]) => unknown
    >
    for (const key of allowedCallbackKeys) {
      self[key] = self[key].bind(self)
    }
  }

  init(callbacks: Partial<Callbacks> = {}) {
    const callbackEntries = (
      Object.entries(callbacks) as [CallbackKey, Callbacks[CallbackKey]][]
    ).filter(([key, value]) => {
      if (!allowedCallbackKeys.includes(key)) {
        console.warn(`Could not set unknown host callback "${key}"`)
        return false
      }

      if (typeof value !== 'function') {
        console.warn(
          `Could not set host callback "${key}" because it is not a function`,
        )
        return false
      }

      return true
    })

    this.callbacksMap = new Map([...this.callbacksMap, ...callbackEntries])
  }

  private executeCallback<
    Key extends CallbackKey,
    Callback extends Callbacks[Key],
  >(key: Key, ...args: Parameters<Callback>): ReturnType<Callback> {
    const callback = this.callbacksMap.get(key) ?? defaultCallbacks[key]
    return (callback as WrappedCallback<Callback>)(...args)
  }

  hasPermission(permission: string) {
    return this.executeCallback('hasPermission', permission)
  }

  hasPermissions(permissions: string[]) {
    return this.executeCallback('hasPermissions', permissions)
  }

  hasRole(role: string) {
    return this.executeCallback('hasRole', role)
  }

  hasRoles(roles: string[]) {
    return this.executeCallback('hasRoles', roles)
  }

  hasFeature(flag: string) {
    return this.executeCallback('hasFeature', flag)
  }

  hasDevFlag(flag: string) {
    return this.executeCallback('hasDevFlag', flag)
  }

  buildPlatformUrl(slug: string) {
    return this.executeCallback('buildPlatformUrl', slug)
  }

  handleNotFound() {
    return this.executeCallback('handleNotFound')
  }

  getOrgPreferences() {
    return this.executeCallback('getOrgPreferences')
  }

  getAppPreferences(): AppPreferences | null {
    return this.executeCallback('getAppPreferences')
  }

  getSupportEmail() {
    return this.executeCallback('getSupportEmail')
  }

  getShowIfFieldsFor(
    job: unknown,
    customFieldsMapping: unknown,
    additionalFields: unknown,
  ) {
    return this.executeCallback(
      'getShowIfFieldsFor',
      job,
      customFieldsMapping,
      additionalFields,
    )
  }
}

/**
 * This instance is used by Horizon Core to provide callbacks from the host. It
 * should not be exposed for direct use by other packages as some of the methods
 * involve permissions etc so should be somewhat protected.
 *
 * @internal
 */
export const hostCallbacks = new HostCallbacks()

export const {
  hasFeature,
  hasDevFlag,
  buildPlatformUrl,
  hasPermissions,
  hasPermission,
  hasRoles,
  hasRole,
  handleNotFound,
  getOrgPreferences,
  getAppPreferences,
  getSupportEmail,
  getShowIfFieldsFor,
} = hostCallbacks

export const callbacks = Object.fromEntries(
  allowedCallbackKeys.map((key) => [key, hostCallbacks[key]]),
) as unknown as Callbacks

setPlatformGlobals({ callbacks })
